/**
 * What came back — the only shape in which an asset ever learns how it did.
 *
 * One `ItemVerdict` type reaches the asset by two paths: as the reply to
 * `answer()`, and in `init.verdicts` when the task was already graded (review
 * mode, or reload after an exam submit). They are the same data, so the asset
 * has one renderer and no branch (D20).
 *
 * There is no path in the other direction. Nothing here is ever sent by an
 * asset, and no method accepts one.
 */

import { JUDGE_FLAGS } from './scheme.js';
import type { JudgeFlag } from './scheme.js';

/**
 * Which piece of a multi-part answer was right — one entry per position in an `ordering`, one
 * per left-hand entry in a `matching`.
 *
 * **The point of this is the indication, not the arithmetic.** "2 of 4" plus a sentence tells a
 * learner how much they lost; a tick beside two rows and a cross beside the others tells them
 * *what to look at*. Dot2Dot did the latter and it is better teaching, which is the whole
 * reason this exists.
 *
 * `correct` is a boolean and nothing else. No per-part points: under partial credit a part is
 * worth `points ÷ length`, which is not an integer, and scores in this system never accumulate
 * as fractions. The item's `points` and `max` remain the only arithmetic.
 *
 * Facts, so they belong on an attempt record — but a host **must not deliver them in exam
 * mode**, for the same reason it withholds the scheme: "these two are wrong" narrows the
 * answer for a retry.
 */
export interface VerdictPart {
  /**
   * What the part refers to, in the asset's own vocabulary: the choice id for an `ordering`,
   * the left-hand id for a `matching`. So an asset marks the row it already drew, with no
   * mapping table and no positional arithmetic.
   */
  readonly id: string;
  readonly correct: boolean;
}

export interface ItemVerdict {
  readonly itemId: string;
  /**
   * Whether the learner supplied a response at all — see D32. This is the field
   * that keeps "did not answer" and "answered wrong" apart, which the design
   * requires of both the gradebook and the LRS. `correct` cannot carry that
   * distinction: an unanswered auto-graded item is genuinely `correct: false`
   * for grading purposes, since it earns no points.
   */
  readonly answered: boolean;
  /**
   * `null` means *no verdict is available* — a manually graded item awaiting a
   * human, or exam mode where the browser has no scheme. It never means
   * "unanswered"; that is `answered`.
   */
  readonly correct: boolean | null;
  /** Integer, awarded. */
  readonly points: number;
  /** Integer, available. */
  readonly max: number;
  /**
   * From the scheme, resolved to the locale that was active when this verdict was
   * produced. Absent in exam mode.
   *
   * **Presentation, not record (D35).** `itemId`, `answered`, `correct`, `points`
   * and `max` are facts and belong on an attempt record. This string does not: a
   * host that persists it and replays it later shows Hebrew feedback under English
   * questions the moment a learner switches locale, and shows last year's wording
   * after a regrade. Persist the facts; re-resolve the message.
   */
  readonly message?: string;
  /**
   * Per-part detail for the item types that have parts — `ordering` and `matching`. Absent for
   * `choice` and `textEntry`, which have exactly one part and would only repeat `correct`.
   *
   * Optional in both directions: a scorer produces it, and an asset is free to ignore it and
   * show the item-level mark alone. What an asset renders is its own business; what it must
   * always be able to show is whether the item was right.
   */
  readonly parts?: readonly VerdictPart[];
  /**
   * An open answer the AI scored by its rubric (D110): points by criterion id, each 0 to that
   * criterion's points. `points` is the total, which can be less than their sum — the rubric's own
   * rules (an override, a cap) apply to the total. Facts, like `points`.
   */
  readonly scores?: { readonly [criterionId: string]: number };
  /** The AI's notes on the answer for a teacher (D110), e.g. `creative_intent`. Facts, like `points`. */
  readonly flags?: readonly JudgeFlag[];
}

/**
 * The AI's judgement of one open answer (D93) — made host side, never by the asset, and handed to
 * the scorer as an input, as a seed is, so scoring stays pure (invariant 7).
 */
export interface Judgement {
  readonly itemId: string;
  /** The answer judged, as it was reported. A judgement is for this text and no other. */
  readonly text: string;
  /** Integer, awarded: 0 to `max`. */
  readonly points: number;
  /** The item's points when it was judged; a judgement for other points is not used. */
  readonly max: number;
  readonly pass: boolean;
  /** What the answer did well, and at most one thing to fix — the verdict's message. */
  readonly strength?: string;
  readonly fix?: string;
  /** Points by rubric criterion, when the AI scored the rubric (D110); `points` is then its total. */
  readonly scores?: { readonly [criterionId: string]: number };
  /** The AI's notes for a teacher (D110). */
  readonly flags?: readonly JudgeFlag[];
  /** The judging server's signature, so a submit can take it as given; absent from a mock. */
  readonly sig?: string;
}

/** Is this a judgement at all? Checked on data from a page, which is never trusted (D93). */
export function isJudgement(value: unknown): value is Judgement {
  if (value === null || typeof value !== 'object') return false;
  const j = value as Record<string, unknown>;
  const optionalText = (key: string): boolean => j[key] === undefined || typeof j[key] === 'string';
  return (
    typeof j['itemId'] === 'string' &&
    typeof j['text'] === 'string' &&
    Number.isInteger(j['points']) &&
    Number.isInteger(j['max']) &&
    typeof j['pass'] === 'boolean' &&
    optionalText('strength') &&
    optionalText('fix') &&
    optionalText('sig') &&
    (j['scores'] === undefined || isScores(j['scores'])) &&
    (j['flags'] === undefined || isFlags(j['flags']))
  );
}

/** Points by criterion: whole numbers, none negative. */
export function isScores(value: unknown): value is { readonly [criterionId: string]: number } {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    && Object.values(value).every((points) => Number.isInteger(points) && (points as number) >= 0);
}

/** Known flags only, each once. */
export function isFlags(value: unknown): value is readonly JudgeFlag[] {
  return Array.isArray(value) && new Set(value).size === value.length
    && value.every((flag) => (JUDGE_FLAGS as readonly unknown[]).includes(flag));
}

export interface Score {
  /** Integer. */
  readonly raw: number;
  /** Integer. */
  readonly max: number;
  /**
   * `raw / max`, derived on the way out for a host that needs it — xAPI wants a
   * fraction. Nothing inside the scorer reads it and nothing compares against
   * it, because scores never accumulate as floats.
   */
  readonly scaled: number;
  /** Present only when the scheme declares `aggregate.passing`. */
  readonly passed?: boolean;
}

/**
 * What `POST /v1/score` returns. A pure function of `{ scheme, responses }` —
 * no lookup, no database, no learner identity.
 */
export interface ScoreResult {
  readonly total: Score;
  /** Each part's score, when the scheme declares `aggregate.parts` (D93); absent otherwise. */
  readonly parts?: { readonly [partId: string]: Score };
  readonly perItem: readonly ItemVerdict[];
  /**
   * Which rules produced this. Pinned per attempt so a regrade months later can
   * reproduce what the student actually saw.
   */
  readonly scoringVersion: string;
}
